import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Building2, Landmark, Flame, ShieldCheck,
  Globe2, Users2,
  ArrowRight, Award, Scale, CheckCircle2,
} from "lucide-react";
import SeoHead, { orgSchema, breadcrumbSchema } from "@/components/SeoHead";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import LeadModal from "@/components/LeadModal";
import HiBehindAi from "@/components/HiBehindAi";
import FundingStrip from "@/components/FundingStrip";
import { SITE } from "@/lib/seoData";

const aboutPageSchema = () => ({
  "@context": "https://schema.org",
  "@type": "AboutPage",
  "@id": `${SITE.url}/about#aboutpage`,
  url: `${SITE.url}/about`,
  name: "About OzyLoans",
  description:
    "OzyLoans is Australia's commercial credit platform with over 50 years of combined banking and finance experience across Australia, New Zealand and India.",
  mainEntity: { "@id": `${SITE.url}/#organization` },
  isPartOf: { "@id": `${SITE.url}/#website` },
});

const SPECIALISMS = [
  { icon: Building2, title: "Commercial Mortgages", desc: "Purchase, refinance and equity release across retail, office, industrial and medical property Australia-wide." },
  { icon: Landmark,  title: "Development Funding",  desc: "Land, construction, residual stock and mezzanine finance for property developers." },
  { icon: Flame,     title: "Bridging Finance",     desc: "Short-term secured finance when banks can't move fast enough — 5 to 30 day settlements." },
  { icon: ShieldCheck, title: "Private Mortgages",  desc: "Second-mortgage, caveat and private-lender solutions for complex or time-critical scenarios." },
];

const NUMBERS = [
  { icon: Users2,    value: "50+",  label: "Years combined experience" },
  { icon: Globe2,    value: "3",    label: "Countries" },
  { icon: Award,     value: "#485752", label: "ACL" },
  { icon: Scale,     value: "#618333", label: "AFCA" },
  { icon: ShieldCheck, value: "#246073", label: "MFAA" },
];

const PROCESS = [
  { step: "01", title: "Tell us about your scenario", desc: "A short call or online enquiry — funding purpose, security, timing, credit position. No paperwork upfront." },
  { step: "02", title: "We assess and structure",     desc: "We qualify the deal against our lender panel and design the structure most likely to settle at the pricing you need." },
  { step: "03", title: "We match, submit and settle", desc: "We match to the right lender, manage the submission end-to-end, and drive the deal to settlement." },
];

const TEAM = [
  {
    name: "Mark (Munaf) Shaikh",
    role: "Founder & Director",
    bio: [
      "Accredited finance broker since 2006",
      "MFAA Full Member #246073",
      "Former National Australia Bank",
      "Career across New Zealand and Australia",
    ],
  },
  {
    name: "Sarah Shaikh",
    role: "Operational Risk",
    bio: [
      "Former ANZ",
      "Former National Australia Bank",
      "Credit · Controls · Commercial Broking",
      "Career across India and Australia",
    ],
  },
];

const COMPLIANCE = [
  { icon: Award,       title: "Australian Credit Licence", detail: `#${SITE.acl}` },
  { icon: Scale,       title: "AFCA Membership",           detail: `#${SITE.afca}` },
  { icon: ShieldCheck, title: "MFAA Full Member",          detail: `#${SITE.mfaa}` },
  { icon: CheckCircle2, title: "Responsible Lending",       detail: "Assessments comply with NCCP obligations" },
  { icon: CheckCircle2, title: "Lender Approval Applies",   detail: "All finance subject to individual lender approval" },
  { icon: CheckCircle2, title: "Professional Indemnity",    detail: "Coverage details available on request" },
];

export default function About() {
  const [open, setOpen] = useState(false);

  return (
    <div className="bg-black text-white min-h-screen">
      <SeoHead
        title="About OzyLoans | Commercial Finance Experts Australia"
        description="Learn about OzyLoans, Australia's commercial credit platform with over 50 years of combined banking and finance experience across Australia, New Zealand and India."
        path="/about"
        schemas={[
          orgSchema(),
          aboutPageSchema(),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "About", path: "/about" },
          ]),
        ]}
      />
      <SiteHeader />

      {/* HERO */}
      <section className="pt-24 pb-16 sm:pt-32 sm:pb-24 px-6 border-b border-zinc-900" data-testid="about-hero">
        <div className="max-w-5xl mx-auto">
          <p className="text-xs uppercase tracking-[0.4em] text-[#E50914] mb-5">About OzyLoans</p>
          <h1 className="font-bebas text-5xl sm:text-6xl md:text-7xl leading-[0.95] tracking-tight text-white max-w-4xl">
            50 years of banking &amp; finance experience.<br className="hidden sm:inline" />
            <span className="text-[#E50914]"> Three countries. </span>
            One job — get you funded.
          </h1>
          <div className="mt-8 max-w-2xl text-zinc-300 text-lg space-y-2">
            <p>Commercial mortgages. Development funding.</p>
            <p>Bridging finance. Private credit.</p>
            <p className="text-zinc-500">That&apos;s what we do. Nothing else.</p>
          </div>
        </div>
      </section>

      {/* SECTION 1 — Who We Are */}
      <section className="py-20 sm:py-28 px-6" data-testid="about-who">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-bebas text-4xl sm:text-5xl leading-[0.95] tracking-tight mb-8">Who we are</h2>
          <div className="space-y-5 text-zinc-300 text-base sm:text-lg leading-relaxed">
            <p>
              OzyLoans is an Australian commercial credit platform built by career bankers and brokers with more than 50 years of combined experience across Australia, New Zealand and India.
            </p>
            <p>
              We're not a call centre and we're not a comparison site. We capture your enquiry, qualify the scenario in-house, and route the deal to the right lender from our panel of capital partners — banks, non-banks and <Link to="/finance/private-credit" className="text-[#E50914] underline underline-offset-4 decoration-[#E50914]/40 hover:text-white transition-colors">private credit</Link>.
            </p>
            <p>
              Our founders have been on the desk at NAB and ANZ, structuring commercial and property credit at scale. That inside-the-tent view is why our submissions land ready-to-approve and our clients settle when others get stuck.
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 2 — What We Do */}
      <section className="py-20 sm:py-28 px-6 border-t border-zinc-900 bg-[#0a0a0a]" data-testid="about-what">
        <div className="max-w-6xl mx-auto">
          <p className="text-xs uppercase tracking-[0.4em] text-[#E50914] mb-4">What we do</p>
          <h2 className="font-bebas text-4xl sm:text-5xl leading-[0.95] tracking-tight mb-12">Four specialisms. No distractions.</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {SPECIALISMS.map((s) => (
              <div key={s.title} className="bg-[#0c0c0c] border border-zinc-800/70 rounded-2xl p-6 hover:border-[#E50914]/50 transition-colors" data-testid={`about-specialism-${s.title.toLowerCase().replace(/[^a-z]/g, '-')}`}>
                <s.icon className="w-7 h-7 text-[#E50914] mb-5" />
                <h3 className="text-lg font-semibold mb-2">{s.title}</h3>
                <p className="text-sm text-zinc-400 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 3 — The Numbers */}
      <section className="py-20 sm:py-28 px-6 border-t border-zinc-900" data-testid="about-numbers">
        <div className="max-w-6xl mx-auto">
          <p className="text-xs uppercase tracking-[0.4em] text-[#E50914] mb-4">The numbers</p>
          <h2 className="font-bebas text-4xl sm:text-5xl leading-[0.95] tracking-tight mb-12">Track record and credentials.</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-5">
            {NUMBERS.map((n) => (
              <div key={n.label} className="bg-[#0c0c0c] border border-zinc-800/70 rounded-2xl p-5 sm:p-6" data-testid={`about-stat-${n.label.toLowerCase().replace(/[^a-z]/g, '-')}`}>
                <n.icon className="w-5 h-5 text-[#E50914] mb-3" />
                <div className="font-bebas text-3xl sm:text-4xl leading-none tracking-tight text-white">{n.value}</div>
                <div className="text-xs uppercase tracking-widest text-zinc-500 mt-2">{n.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 4 — How We Work */}
      <section className="py-20 sm:py-28 px-6 border-t border-zinc-900 bg-[#0a0a0a]" data-testid="about-process">
        <div className="max-w-5xl mx-auto">
          <p className="text-xs uppercase tracking-[0.4em] text-[#E50914] mb-4">How we work</p>
          <h2 className="font-bebas text-4xl sm:text-5xl leading-[0.95] tracking-tight mb-12">A clean three-step process.</h2>
          <div className="space-y-6">
            {PROCESS.map((p) => (
              <div key={p.step} className="grid grid-cols-[64px_1fr] sm:grid-cols-[96px_1fr] gap-5 sm:gap-8 items-start pb-6 border-b border-zinc-900 last:border-b-0" data-testid={`about-step-${p.step}`}>
                <div className="font-bebas text-4xl sm:text-6xl leading-none text-[#E50914]">{p.step}</div>
                <div>
                  <h3 className="text-xl sm:text-2xl font-semibold text-white mb-2">{p.title}</h3>
                  <p className="text-zinc-400 leading-relaxed">{p.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 5 — Meet the Team */}
      <section className="py-20 sm:py-28 px-6 border-t border-zinc-900" data-testid="about-team">
        <div className="max-w-6xl mx-auto">
          <p className="text-xs uppercase tracking-[0.4em] text-[#E50914] mb-4">Meet the team</p>
          <h2 className="font-bebas text-4xl sm:text-5xl leading-[0.95] tracking-tight mb-12">The people behind the deals.</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {TEAM.map((m) => (
              <article key={m.name} className="bg-[#0c0c0c] border border-zinc-800/70 rounded-2xl p-7" data-testid={`about-team-${m.name.split(' ')[0].toLowerCase()}`}>
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#E50914] to-[#A50710] flex items-center justify-center text-white font-bebas text-2xl mb-5">
                  {m.name.replace(/\([^)]*\)/g, "").split(/\s+/).filter(Boolean).map((s) => s[0]).slice(0, 2).join("")}
                </div>
                <h3 className="text-lg font-semibold text-white">{m.name}</h3>
                <p className="text-[#E50914] text-sm mb-4">{m.role}</p>
                <ul className="space-y-1.5 text-sm text-zinc-400">
                  {m.bio.map((b) => <li key={b}>{b}</li>)}
                </ul>
              </article>
            ))}
            {/* Placeholder card for future team members */}
            <article className="bg-[#0a0a0a] border border-dashed border-zinc-800 rounded-2xl p-7 flex flex-col items-center justify-center text-center min-h-[240px]" data-testid="about-team-placeholder">
              <Users2 className="w-8 h-8 text-zinc-700 mb-3" />
              <p className="text-sm text-zinc-500">More team members joining soon.</p>
            </article>
          </div>
        </div>
      </section>

      {/* SECTION 6 — Compliance & Trust */}
      <section className="py-20 sm:py-28 px-6 border-t border-zinc-900 bg-[#0a0a0a]" data-testid="about-compliance">
        <div className="max-w-6xl mx-auto">
          <p className="text-xs uppercase tracking-[0.4em] text-[#E50914] mb-4">Compliance &amp; trust</p>
          <h2 className="font-bebas text-4xl sm:text-5xl leading-[0.95] tracking-tight mb-12">Licensed, accredited, insured.</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {COMPLIANCE.map((c) => (
              <div key={c.title} className="bg-[#0c0c0c] border border-zinc-800/70 rounded-2xl p-6" data-testid={`about-compliance-${c.title.toLowerCase().replace(/[^a-z]/g, '-')}`}>
                <c.icon className="w-6 h-6 text-[#E50914] mb-4" />
                <h3 className="text-base font-semibold text-white mb-1.5">{c.title}</h3>
                <p className="text-sm text-zinc-400">{c.detail}</p>
              </div>
            ))}
          </div>
          <p className="text-xs text-zinc-600 mt-8 max-w-2xl leading-relaxed">
            All finance is subject to responsible-lending obligations and individual lender approval. Full complaints process available via our <Link to="/complaints" className="underline hover:text-white">Complaints page</Link>. Read our <Link to="/credit-guide" className="underline hover:text-white">Credit Guide</Link> for fees and commission disclosure.
          </p>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-24 sm:py-32 px-6 border-t border-zinc-900" data-testid="about-cta">
        <div className="max-w-4xl mx-auto text-center">
          <HiBehindAi size="section" className="inline-block mb-5" testid="about-hi-behind-ai" />
          <h2 className="font-bebas text-4xl sm:text-6xl leading-[0.95] tracking-tight text-white mb-6">
            Let&apos;s talk about your next opportunity.
          </h2>
          <p className="text-zinc-400 text-lg mb-10 max-w-2xl mx-auto">
            A finance professional reviews your requirements and objectives. No credit check, no obligation.
          </p>
          <button
            type="button"
            onClick={() => setOpen(true)}
            data-testid="about-cta-button"
            className="h-14 px-8 rounded-full bg-[#E50914] hover:bg-[#F6121D] text-white font-semibold text-base inline-flex items-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black"
          >
            Start your funding assessment <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </section>

      <FundingStrip />
      <SiteFooter />
      <LeadModal open={open} onOpenChange={setOpen} sourceChannel="about_page" />
    </div>
  );
}
